package us.laantech.admin.menu.clients.products

import androidx.compose.runtime.snapshots.SnapshotStateMap
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.ionspin.kotlin.bignum.decimal.toBigDecimal
import io.github.vinceglb.filekit.PlatformFile
import io.github.vinceglb.filekit.readBytes
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.parameter
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.channels.ReceiveChannel
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import us.laantech.common.dto.RetailProductDTO
import us.laantech.common.dto.company.CompanyBranch
import us.laantech.common.dto.company.Department
import us.laantech.common.util.toBooleanLooseOrNull
import us.laantech.common.util.toLongCentsFromDollars
import us.laantech.common.api.KtorClient
import kotlin.time.Duration.Companion.milliseconds

class RetailProductsManagerViewModel(accountSub:String?, companyBranch: CompanyBranch): ViewModel() {

    val UPLOAD_BATCH_SIZE = 25
    val _uiState =
        MutableStateFlow(RetailProductsManagerUiState(accountSub = accountSub, companyBranch = companyBranch,))
    val uiState = _uiState.asStateFlow()
    val ktClient = KtorClient.instance.client

    var currentPage = 1

    suspend fun loadDepartments() {
        val response = ktClient.get("/admin/clients/retail-products/departments/list") {
            parameter(RetailProductDTO::branchUuid.name, uiState.value.companyBranch.uuid)
        }
        val departments = response.body<List<Department>>()
        _uiState.update { it.copy(departments = departments) }
    }

    fun loadProducts(page: Int = 1) {
        viewModelScope.launch {
            val response = ktClient.get("/admin/clients/retail-products/list") {
                parameter("page", page)
                parameter(RetailProductDTO::branchUuid.name, uiState.value.companyBranch.uuid)
            }
            val products = response.body<List<RetailProductDTO>>()
            _uiState.update { it.copy(products = products) }
        }
    }

    fun loadNextPage(previous: Boolean = false) {
        if (previous) {
            if (currentPage <= 1) return
            currentPage--
        } else {
            currentPage++
        }
        loadProducts(currentPage)
    }


    fun loadFile(file: PlatformFile) {
        viewModelScope.launch {
            val text = file.readBytes().decodeToString()
            _uiState.update {
                val csv = CSV(text)
                //uiState.csv?.dataRows()?.count()
                it.copy(
                    csv = csv,
                    numRows = csv.dataRows().count().toLong()
                )
            }
            toggleShowUploadProductsModal()
        }
    }

    fun toggleShowUploadProductsModal() {
        viewModelScope.launch {
            _uiState.update {
                it.copy(showUploadProductsModal = !it.showUploadProductsModal)
            }
        }
    }

    // Consumer: collect batches of UPLOAD_BATCH_SIZE, upload with retry, advance progress.
    // Launched on the caller's scope (the per-run coroutineScope) so a failure
    // here propagates to createProducts' catch and cancels the producer.
    private fun CoroutineScope.launchUploadConsumer(
        channel: ReceiveChannel<RetailProductDTO>,
    ): Job = launch {
        val batch = mutableListOf<RetailProductDTO>()
        suspend fun flush() {
            if (batch.isEmpty()) return
            val inserted = uploadBatchWithRetry(batch.toList())   // throws once retries are exhausted
            _uiState.update {
                it.copy(numRowsProcessed = it.numRowsProcessed + inserted)
            }
            batch.clear()
        }
        for (dto in channel) {                     // suspends when empty, exits on close()
            batch += dto
            if (batch.size >= UPLOAD_BATCH_SIZE) flush()
        }
        flush()                                    // final partial batch
    }

    // Builds a DTO from a single row. `cell` resolves a target field name to its
    // value in this row (or null if unmapped / missing) — bound to the row by the caller.
    private fun createRetailProduct(cell: (String) -> String?): RetailProductDTO {
        // most likely in dollars and may contain a dollar sign, cleanup
        val csvPrice = cell(RetailProductDTO::priceCents.name)
            ?.replace("$", "")

        val isDollars = csvPrice?.contains('.') == true
        val priceCents = if (isDollars) {
            csvPrice.toBigDecimal().toLongCentsFromDollars()
        } else {
            csvPrice?.toLongOrNull()
        }

        // The CSV holds the department *name*; resolve it to the UUID the API expects.
        val departmentName = cell("department")?.trim()
        val departmentUUID = uiState.value.departments.firstOrNull {
            it.departmentName == departmentName }?.departmentUUID

        // Create the DTO
        // API needs to find accountSub from branch
        // API needs to find departmentUUID from name
        return RetailProductDTO(
            accountSub = uiState.value.accountSub,
            branchUuid = uiState.value.companyBranch.uuid,
            priceCents = priceCents,
            // string field — direct:
            title = cell(RetailProductDTO::title.name),
            barcode = cell(RetailProductDTO::barcode.name),
            // non-string fields need conversion:
            ageLimit = cell(RetailProductDTO::ageLimit.name)?.trim()?.toIntOrNull(),
            ebt = cell(RetailProductDTO::ebt.name)?.toBooleanLooseOrNull(),
            taxable = cell(RetailProductDTO::taxable.name)?.toBooleanLooseOrNull(),
            departmentUUID = departmentUUID,
        )
    }


    private suspend fun uploadBatchWithRetry(
        products: List<RetailProductDTO>,
        maxAttempts: Int = 3,
    ): Int {
        var attempt = 0
        var backoff = 500L
        while (true) {
            attempt++
            try {
                val response = ktClient.post("/admin/clients/retail-products") {
                    contentType(ContentType.Application.Json)
                    setBody(products)
                }
                if (response.status.isSuccess()) return response.body<Int>()
                if (attempt >= maxAttempts) {
                    error("Upload failed (${response.status}) after $attempt attempts")
                }
            } catch (e: CancellationException) {
                throw e                                            // never swallow cancellation
            } catch (e: Exception) {
                if (attempt >= maxAttempts) throw e
            }
            delay(backoff.milliseconds)
            backoff *= 2                                            // exponential backoff
        }
    }


    // Create departments from the CSV data.
    suspend fun createDepartments(cell: (row: List<String>, targetColumn: String) -> String?) {
        val csv = uiState.value.csv ?: return

        // Unique, non-blank department names from the mapped column.
        val departmentNames = csv.dataRows()
            .mapNotNull { row -> cell(row, "department")?.trim()?.takeIf { it.isNotEmpty() } }
            .distinct()
            .toList()

        val response = ktClient.post("/admin/clients/retail-products/departments") {
            contentType(ContentType.Application.Json)
            parameter(RetailProductDTO::branchUuid.name, uiState.value.companyBranch.uuid)
            setBody(departmentNames)
        }
        require(response.status.isSuccess()) {
            "Failed to create departments"
        }
        // reload
        loadDepartments()
    }



    fun createProducts(mapping: SnapshotStateMap<String, String>) {
        viewModelScope.launch {
            val csv = uiState.value.csv ?: return@launch
            val colIndex = csv.listHeaders()
                .withIndex()
                .associate { (i, name) -> name to i }

            // Resolve a target field to its value in a given row. Defined once;
            // the producer binds it to each row below.
            // returns the cell that corresponds to the column of the given row
            fun cell(row: List<String>, targetColumn: String): String? {
                val sourceCol = mapping[targetColumn] ?: return null   // user didn't map this field
                val idx = colIndex[sourceCol] ?: return null           // mapped column not in header
                return row.getOrNull(idx)                              // row shorter than expected
            }

            // Create departments first so the API can resolve departmentUUID by name.
            createDepartments(::cell)

            _uiState.update { it.copy(uploadError = null, numRowsProcessed = 0) }

            // Bounded channel, fresh per run: send() suspends when full, giving the
            // parser backpressure against a slower API so we never buffer the whole
            // CSV in memory. A local val means each upload run gets an open channel.
            val channel = Channel<RetailProductDTO>(100)

            try {
                coroutineScope {
                    val uploader = launchUploadConsumer(channel)

                    // Producer: parse each row and hand it off; send() applies backpressure.
                    csv.dataRows().forEach { row ->
                        val product = createRetailProduct { target -> cell(row, target) }
                        channel.send(product)
                    }
                    channel.close()                                // no more items
                    uploader.join()                                // wait for the last batch to upload
                }
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                channel.close(e)
                _uiState.update { it.copy(uploadError = e.message ?: "Upload failed") }
            }

            toggleShowUploadProductsModal()
            loadDepartments()
            loadProducts()
        }
    }
}